import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import P2PFloorRow, { duelHeat } from "./P2PFloorRow";

const SWIPE_THRESHOLD = 52;

export default function P2PFloorIndex({
  duels,
  selectedId,
  onSelect,
  desk,
  pagination,
  loading,
  onPageChange,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);
  const touchRef = useRef({ x: 0, y: 0 });
  const [slideDir, setSlideDir] = useState(null);

  const { page = 1, totalPages = 1, total = 0, limit = 15 } = pagination || {};
  const startIndex = (page - 1) * limit;
  const endIndex = Math.min(startIndex + duels.length, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const maxPool = useMemo(() => {
    if (!duels.length) return 1;
    return Math.max(1, ...duels.map((d) => Number(d.poolTotal) || 0));
  }, [duels]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [page]);

  useEffect(() => {
    if (!slideDir) return undefined;
    const timer = window.setTimeout(() => setSlideDir(null), 220);
    return () => window.clearTimeout(timer);
  }, [slideDir, page]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const r = selectedRef.current.getBoundingClientRect();
      const c = listRef.current.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  const goPage = (nextPage) => {
    if (loading || nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setSlideDir(nextPage > page ? "next" : "prev");
    onPageChange(nextPage);
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    if (totalPages <= 1) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - touchRef.current.x;
    const dy = touch.clientY - touchRef.current.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goPage(page + 1);
    else goPage(page - 1);
  };

  return (
    <aside className="amf-index ap2p-index" aria-label="Duel ledger">
      <div className="amf-index-head" aria-live="polite">
        <div className="amf-index-head-row">
          <span className="amf-index-brand-label">Duel ledger</span>
          <span className="amf-index-count">
            {total > 0 ? (
              <>
                <strong>
                  {startIndex + 1}–{endIndex}
                </strong>
                {" / "}
                <strong>{total}</strong>
              </>
            ) : (
              <strong>{duels.length}</strong>
            )}
            {totalPages > 1 ? ` · p${page}` : ""}
            {desk === "needs_outcome" ? " · queue" : ""}
          </span>
        </div>
      </div>

      <div
        ref={listRef}
        className={`amf-index-list ap2p-index-list${slideDir ? ` ap2p-index-list-${slideDir}` : ""}${loading ? " ap2p-index-list-loading" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {loading && duels.length === 0 ? (
          <div className="ap2p-index-loading">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading page…</span>
          </div>
        ) : duels.length === 0 ? (
          <div className="amf-index-empty">No duels match current filters.</div>
        ) : (
          duels.map((duel, idx) => {
            const id = duel._id;
            const heatPct = duelHeat(duel, maxPool);
            return (
              <div key={id} ref={id === selectedId ? selectedRef : null}>
                <P2PFloorRow
                  duel={duel}
                  rank={startIndex + idx + 1}
                  selected={id === selectedId}
                  onSelect={() => onSelect(id)}
                  heatPct={heatPct}
                />
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 ? (
        <div className="ap2p-index-foot">
          <span className="ap2p-index-foot-meta">Swipe list or use arrows</span>
          <div className="ap2p-index-pager">
            <button
              type="button"
              className="ap2p-pager-btn"
              disabled={!canPrev || loading}
              onClick={() => goPage(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} aria-hidden />
            </button>
            <span className="ap2p-pager-label">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className="ap2p-pager-btn"
              disabled={!canNext || loading}
              onClick={() => goPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={16} aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
